import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import { LoginPage } from "@/pages/LoginPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { PaymentsPage } from "@/pages/PaymentsPage";
import { PaymentDetailPage } from "@/pages/PaymentDetailPage";
import { MerchantOverviewPage } from "@/pages/MerchantOverviewPage";
import { MerchantSettingsPage } from "@/pages/MerchantSettingsPage";
import { PricingPage } from "@/pages/PricingPage";
import { WalletBalancesPage } from "@/pages/WalletBalancesPage";
import { WithdrawalInitiatePage } from "@/pages/WithdrawalInitiatePage";
import { WithdrawalHistoryPage } from "@/pages/WithdrawalHistoryPage";
import { MerchantTeamPage } from "@/pages/MerchantTeamPage";
import { MerchantApiKeysPage } from "@/pages/MerchantApiKeysPage";
import { StatementsPage } from "@/pages/StatementsPage";
import { HistoryPage } from "@/pages/HistoryPage";
import { TransactionStatusesPage } from "@/pages/TransactionStatusesPage";
import { UserRolesPage } from "@/pages/UserRolesPage";
import { FunnelPage } from "@/pages/FunnelPage";
import { HelpPage } from "@/pages/HelpPage";
import { SupportListPage } from "@/pages/SupportListPage";
import { SupportDetailPage } from "@/pages/SupportDetailPage";
import { UpdateBanner } from "@/components/UpdateBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function Shell({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: readonly any[] }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles as any}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/** Redirige `/` selon l'auth : Vue d'ensemble si connecté, sinon Login. */
function HomeRedirect() {
  const auth = useAuth();
  if (auth.status === "loading") return null;
  return auth.status === "authenticated"
    ? <Navigate to="/merchant/overview" replace />
    : <Navigate to="/login" replace />;
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="text-5xl font-bold text-brand-700 mb-2">404</div>
        <div className="text-slate-600">Page non trouvée</div>
      </div>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center max-w-md">
        <div className="text-5xl font-bold text-red-700 mb-2">403</div>
        <div className="text-slate-700 font-medium mb-1">Accès refusé</div>
        <div className="text-sm text-slate-500">Votre rôle n'a pas la permission d'accéder à cette page.</div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <UpdateBanner />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Transactions */}
            <Route
              path="/payments"
              element={<Shell allowedRoles={["commercial", "finance", "it", "merchant_admin"] as const}><PaymentsPage /></Shell>}
            />
            <Route
              path="/payments/:id"
              element={<Shell allowedRoles={["commercial", "finance", "it", "merchant_admin"] as const}><PaymentDetailPage /></Shell>}
            />
            <Route
              path="/funnel"
              element={<Shell allowedRoles={["commercial", "finance", "it", "merchant_admin"] as const}><FunnelPage /></Shell>}
            />
            <Route
              path="/transactions/statuses"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><TransactionStatusesPage /></Shell>}
            />

            {/* Merchant portal (self-service) */}
            <Route
              path="/merchant/overview"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><MerchantOverviewPage /></Shell>}
            />
            <Route
              path="/merchant/pricing"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><PricingPage /></Shell>}
            />
            <Route
              path="/finance/wallet"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><WalletBalancesPage /></Shell>}
            />
            <Route
              path="/finance/withdrawal"
              element={<Shell allowedRoles={["merchant_admin", "finance"] as const}><WithdrawalInitiatePage /></Shell>}
            />
            <Route
              path="/finance/withdrawal/history"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><WithdrawalHistoryPage /></Shell>}
            />
            <Route
              path="/merchant/settings"
              element={<Shell allowedRoles={["merchant_admin"] as const}><MerchantSettingsPage /></Shell>}
            />
            <Route
              path="/merchant/team"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><MerchantTeamPage /></Shell>}
            />
            <Route
              path="/merchant/api-keys"
              element={<Shell allowedRoles={["it"] as const}><MerchantApiKeysPage /></Shell>}
            />
            <Route
              path="/merchant/roles"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><UserRolesPage /></Shell>}
            />

            {/* Finance */}
            <Route
              path="/finance/statements"
              element={<Shell allowedRoles={["merchant_admin", "finance"] as const}><StatementsPage /></Shell>}
            />
            <Route
              path="/finance/history"
              element={<Shell allowedRoles={["merchant_admin", "finance"] as const}><HistoryPage /></Shell>}
            />

            {/* Help center */}
            <Route
              path="/help"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><HelpPage /></Shell>}
            />

            {/* Support tickets */}
            <Route
              path="/support"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><SupportListPage /></Shell>}
            />
            <Route
              path="/support/:ticketId"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><SupportDetailPage /></Shell>}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
